/**
 * Signed-in user menu for light top bars (the main dashboard): name + avatar
 * open Profile, Settings and Log out. Same actions as the sidebar's user menu.
 */
import { useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, Settings, User as UserIcon } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './Avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from './DropdownMenu';
import { useAuthStore } from '../store/authStore';

const initials = (name?: string) =>
  (name || 'User').split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join('');

export function UserMenu() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  const signOut = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="flex items-center gap-2 rounded-lg px-1.5 py-1 text-left outline-none transition-colors hover:bg-secondary focus-visible:ring-2 focus-visible:ring-ring/50"
        aria-label="Account menu"
      >
        <span className="hidden text-sm font-medium text-foreground sm:inline-block">{user?.name || 'TeamOne'}</span>
        <Avatar size="sm">
          {user?.avatar && <AvatarImage src={user.avatar} alt={user?.name} />}
          <AvatarFallback>{initials(user?.name)}</AvatarFallback>
        </Avatar>
        <ChevronDown className="size-3.5 text-muted-foreground" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="normal-case">
          <p className="text-sm font-medium text-foreground">{user?.name || 'User'}</p>
          <p className="truncate text-xs font-normal text-muted-foreground">{user?.email}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/profile')}>
          <UserIcon className="size-4" /> Profile
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/settings')}>
          <Settings className="size-4" /> Settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem destructive onClick={signOut}>
          <LogOut className="size-4" /> Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
